/**
 * FilterDisclosure class manages the behavior of filter disclosure elements.
 * Each instance represents a single filter component.
 */
export class FilterDisclosure {
  constructor(el) {
    this.container = el;
    this.button = el.querySelector('.filter-disclosure__button');
    this.target = el.querySelector(
      `#${this.button.getAttribute('aria-controls')}`
    );
    this.checks = [
      ...this.target.querySelectorAll('.filter-disclosure__checkbox'),
    ];
    this.checkedCount = 0;

    this.addEventListeners();
    this.setupDisclosure();
  }

  /**
   * Adds event listeners to the component elements.
   */
  addEventListeners() {
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDisclosure();
    });

    this.checks.forEach((check) =>
      check.addEventListener('click', (e) => {
        this.handleCheckChange(
          e.currentTarget.hasAttribute('data-js-filter-all')
        );
      })
    );

    this.container.addEventListener('reset-filter', (e) => {
      this.handleCheckChange(true);
      this.toggleDisclosure('close');
    });

    document.addEventListener('keyup', (e) => {
      this.handleKeyup(e);
    });
    document.addEventListener('click', (e) => {
      this.handleClickOut(e);
    });
  }

  /**
   * Handles changes to checkbox selection.
   * @param {boolean} isAllOption - Whether the clicked checkbox is the "select all" option.
   */
  handleCheckChange(isAllOption) {
    if (isAllOption) {
      this.clearFilter();
    }

    this.setCheckCount();
    this.updateButtonText();
    this.handleAllOption();
  }

  /**
   * Handles Escape key press to close the disclosure.
   * @param {KeyboardEvent} evt - The key press event.
   */
  handleKeyup(evt) {
    let isEscape = false;
    if ('key' in evt) {
      isEscape = evt.key == 'Escape' || evt.key == 'Esc';
    } else {
      isEscape = evt.keyCode == 27;
    }

    if (isEscape) {
      this.toggleDisclosure('close');
    }
  }

  /**
   * Handles clicks outside the component to close the disclosure.
   * @param {MouseEvent} evt - The click event.
   */
  handleClickOut(evt) {
    const containsFocus = this.container.contains(evt.target);
    if (!containsFocus) {
      this.toggleDisclosure('close');
    }
  }

  setupDisclosure() {
    this.updateButtonText();
    this.handleAllOption();
    this.button.setAttribute('aria-expanded', 'false');
  }

  clearFilter() {
    this.checks.forEach((check) => (check.checked = false));
  }

  setCheckCount() {
    this.checkedCount = this.checks.filter(
      (el) => el.checked && !el.hasAttribute('data-js-filter-all')
    ).length;
  }

  /**
   * Updates the text displayed on the button to reflect the number of selected filters.
   */
  updateButtonText() {
    const counter = this.button.querySelector('.filter-disclosure__counter');
    counter.classList.toggle(
      'filter-disclosure__counter--hidden',
      this.checkedCount === 0
    );
    counter.innerHTML = this.checkedCount;
  }

  handleAllOption() {
    this.target.querySelector('[data-js-filter-all]').checked =
      this.checkedCount === 0;
  }

  /**
   * Show / Hide Disclosure
   * @param {string} force - Override toggle and set state of disclosure widget
   */
  toggleDisclosure(force) {
    let newState = this.button.getAttribute('aria-expanded') == 'false';
    if (force === 'close') {
      newState = false;
    }
    this.button.setAttribute('aria-expanded', newState.toString());
  }
}

const filters = [...document.querySelectorAll('[data-js-filter-disclosure]')];

filters.forEach((filter) => new FilterDisclosure(filter));
